import {MenuItem, useMediaQuery} from '@material-ui/core';
import {CorrespondenceLetterSummary} from 'Agent/Policies/api/apiTypes';
import CorrespondenceDetails from 'Agent/Policies/components/CorrespondenceDetails';
import {
  changeCorrespondenceFilter,
  changeCorrespondenceFilterPopup,
  changeCorrespondenceSummaryFilter,
  changeSort,
  clearCorrespondence,
  clearSort,
  getCorrespondenceDetails,
  getCorrespondenceSummaries,
} from 'Agent/Policies/ducks/policyCorrespondence';
import {theme} from 'Core/components/WithTheme';
import {useBreadcrumbs} from 'Core/hooks/useBreadcrumbs';
import { NotStarted } from 'Core/models/Async';
import * as O from 'fp-ts/lib/Option';
import qs from 'query-string';
import React, {useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useLocation, useRouteMatch} from 'react-router-dom';
import {ReducerState} from 'rootReducer';
import AsyncButton from 'Shared/components/AsyncButton';
import FileDownloadButton, {FileExtension} from 'Shared/components/FileDownloadButton';
import FileDownloadFab from 'Shared/components/FileDownloadFab';
import ActionsMenu from 'Shared/components/PagedView/ActionsMenu';
import {Column} from 'Shared/components/PagedView/Column';
import PagedView from 'Shared/components/PagedView/PagedView';
import {AgencyFilters} from 'Shared/models/AgencyFilters';
import {addFilterParameters} from 'Shared/utilities/addFilterParameters';
import {DataSourceFlag} from 'Shared/utilities/DataSourceUtility';
import {toFormattedDateAsISO, toFormattedDateAsUTC} from 'Shared/utilities/DateFormatter';
import {downloadFile} from 'Shared/utilities/fetchHelpers';
import {prop} from 'Shared/utilities/prop';
import {selectCurrentAgentNumbers} from 'Users/selectors/userProfileSelectors';
import './CorrespondenceFilterModal.sass'

const getFileDownload = (links: CorrespondenceLetterSummary['links']) => links!.find(link => link.rel === 'pdf');
const getDetails = (links: CorrespondenceLetterSummary['links']) => links!.find(link => link.rel === 'details');

/*
 [B-1161632] This is for handling eisFlag received in the download link.
 AB-API change required to remove the eisFlag from query params in download link.
 This code needs to be cleaned up accordingly once API change is done.
*/
export const getDownloadLinkParams = (encryptedIdentifier: string, downloadLink?: string) => {
  const params = new URLSearchParams((downloadLink ?? '').split('?')[1]);
  return params.has(DataSourceFlag.EIS) && params.get(DataSourceFlag.EIS) === 'true'
    ? addFilterParameters({encryptedIdentifier}).replace('?', '&')
    : addFilterParameters({encryptedIdentifier, eisFlag: false});
};

export type CorrespondenceSummariesProps = {
  selectedAgency: AgencyFilters;
  searchCriteria: {}
};

const CorrespondenceSummaries = ({selectedAgency}: CorrespondenceSummariesProps) => {
  const dispatch = useDispatch();
  const match = useRouteMatch();

  useBreadcrumbs('Correspondence', match.url);

  const {correspondenceSummaries, correspondenceSummariesFilter, correspondenceDetails, sort, searchCriteriaValue} = useSelector(
    (state: ReducerState) => state.policyCorrespondence
  );
  const agentNumbers = useSelector(selectCurrentAgentNumbers);
  const {search} = useLocation();
  const agentNumber = qs.parse(search).agentNumber as string;
  const policyNumber = qs.parse(search).policyNumber as string;
  const accountNumber = qs.parse(search).accountNumber as string;


  useEffect(() => {
    if (agentNumber || policyNumber || accountNumber) {
      dispatch(
        changeCorrespondenceSummaryFilter({...correspondenceSummariesFilter, agentNumber, policyNumber, accountNumber})
      );
    }

    return () => {
      dispatch(clearCorrespondence());
      dispatch(clearSort());
    };
  }, [agentNumber]);

  useEffect(() => {
    if((accountNumber || policyNumber) && 
    (!searchCriteriaValue.accountNumber && !searchCriteriaValue.policyNumber)) {
        dispatch(
          changeCorrespondenceFilter({accountNumber: accountNumber, policyNumber: policyNumber})
        );
    }
  })

  useEffect(() => {
      dispatch(
        getCorrespondenceSummaries(
          agentNumbers,
          selectedAgency.isNewYork,
          selectedAgency.encryptedIdentifier,
          undefined,
          correspondenceSummariesFilter,
          O.toUndefined(sort)
        )
      );
  }, [agentNumbers, correspondenceSummariesFilter, sort]);

  useEffect(() => {
      dispatch (
        changeCorrespondenceSummaryFilter({
          ...correspondenceSummariesFilter, 
          policyNumber: searchCriteriaValue.policyNumber, 
          accountNumber: searchCriteriaValue.accountNumber
        })
      );
      dispatch(
        getCorrespondenceSummaries(
          agentNumbers,
          selectedAgency.isNewYork,
          selectedAgency.encryptedIdentifier,
          undefined,
          correspondenceSummariesFilter,
          O.toUndefined(sort)
        )
      );
  }, [searchCriteriaValue]);

  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const exportToCsvProps = {
    startIcon: <i className="fa fa-2x fa-file-download" />,
    extension: FileExtension.CSV,
    downloadedFileName: 'FilteredCorrespondenceSummaries',
    relativeFileLocation: `/api/policies/v1/correspondences${addFilterParameters({
      ...correspondenceSummariesFilter,
      agentNumbers: agentNumbers.length > 0 ? agentNumbers.map(prop('value')) : 'value',
      isNewYork: selectedAgency.isNewYork,
      encryptedIdentifier: selectedAgency.encryptedIdentifier,
      exportToCsv: true,
    })}`,
    children: 'Export To CSV',
  };

  const emptyFilter = Object.values(searchCriteriaValue).every(
    value => value === ''
  );

  const handleFilter = (value: string, label: string) => {
    //@ts-ignore
    dispatch(changeCorrespondenceFilter((values)=>({
      ...values,
      [label]: value
    })));
  }

  return (
    <>
      <CorrespondenceDetails />
      <div className='correspondence_label_filter'>
        <h3 className='correspondences__label'>Correspondences</h3>
        {isMobile && <div className='correspondence_mobile_filter' onClick={()=>dispatch(changeCorrespondenceFilterPopup(true))}>
          <svg 
          xmlns="http://www.w3.org/2000/svg" 
          height="1em" viewBox="0 0 512 512">
            <path d="M487.976 0H24.028C2.71 0-8.047 25.866 7.058 40.971L192 225.941V432c0 7.831 3.821 15.17 10.237 19.662l80 55.98C298.02 518.69 320 507.493 320 487.98V225.941l184.947-184.97C520.021 25.896 509.338 0 487.976 0z"/>
          </svg>
        </div>}
      </div>
      {isMobile && (searchCriteriaValue.accountNumber || searchCriteriaValue.policyNumber) && <div className='correspondence_filters'>
        <span>Searched Filter</span>
        <div className='filters_list'>
          {searchCriteriaValue.accountNumber && <div className='filter_display'>
            <div className='filter__name__value'>
              <div className='title'>Account Number</div>
              <div className='value'>{searchCriteriaValue.accountNumber}</div>
            </div>
            <div className='close__correspondence__filters' onClick={() => handleFilter('', 'accountNumber')}>&#x2715;</div>
          </div>}
          {searchCriteriaValue.policyNumber && <div className='filter_display'>
            <div className='filter__name__value'>
              <div className='title'>Policy Number</div>
              <div className='value'>{searchCriteriaValue.policyNumber}</div>
            </div>
            <div className='close__correspondence__filters' onClick={() => handleFilter('', 'policyNumber')}>&#x2715;</div>
          </div>}  
        </div>
      </div>}
      <PagedView
        pagedViewKey='correspondenceSummaries'
        eventAction='agentCorrespondenceSettingFilter'
        sort={sort ? O.toUndefined(sort) : undefined}
        onChangeSort={sort => {
          dispatch(changeSort(sort));
        }}
        data={
          !emptyFilter ?
          correspondenceSummaries 
          : new NotStarted("Please enter search criteria to get your desired results.")
        }
        loadMore={page =>
          dispatch(
            getCorrespondenceSummaries(
              agentNumbers,
              selectedAgency.isNewYork,
              selectedAgency.encryptedIdentifier,
              page,
              correspondenceSummariesFilter,
              O.toUndefined(sort)
            )
          )
        }
        actions={
          isMobile ? (
            <FileDownloadFab variant="extended" color="primary" displayTooltip {...exportToCsvProps} />
          ) : (
            <FileDownloadButton
              variant="contained"
              color="primary"
              displayTooltip
              {...exportToCsvProps}
              exportToCsvEvent="agentCorrespondence_exportToCsv"
            />
          )
        }
      >
        {columnProps => (
          <>
            <Column
              {...columnProps}
              title="Letter Number"
              sortColumnKey="number"
              default
            >
              {letter => letter.number}
            </Column>
            <Column
              {...columnProps}
              title="Agent Number"
              sortColumnKey="agentNumber"
              default
            >
              {letter => letter.agentNumber}
            </Column>
            <Column
              {...columnProps}
              title="Description"
              sortColumnKey="description"
              default
            >
              {letter => letter.description}
            </Column>
            <Column
              {...columnProps}
              title="Insured Name"
              sortColumnKey="insuredName"
              default
              ddprivacy="mask"
            >
              {letter => letter.insuredName}
            </Column>
            <Column
              {...columnProps}
              title="Policy Number"
              sortColumnKey="policyNumber"
              default
              ddprivacy="mask"
            >
              {letter => letter.policyNumber}
            </Column>
            <Column
              {...columnProps}
              title="Account Number"
              sortColumnKey="accountNumber"
              default
              ddprivacy="mask"
            >
              {letter => letter.accountNumber}
            </Column>
            <Column
              {...columnProps}
              title="Date"
              sortColumnKey="date"
              default
            >
              {letter => toFormattedDateAsUTC(letter.date)}
            </Column>
            <Column {...columnProps} title="" isActions default>
              {letter => {
                const fileDownload = getFileDownload(letter.links);
                const detailsLink = getDetails(letter.links);

                return (
                  <>
                    {detailsLink && (
                      <AsyncButton
                        className="correspondence-summary__view-details-button"
                        data={correspondenceDetails}
                        color="primary"
                        onClick={() =>
                          dispatch(
                            getCorrespondenceDetails(
                              'api/policies' +
                                detailsLink.href! +
                                '&' +
                                addFilterParameters({encryptedIdentifier: selectedAgency.encryptedIdentifier}).slice(1)
                            )
                          )
                        }
                      >
                        View Details
                      </AsyncButton>
                    )}
                    {fileDownload && (
                      <ActionsMenu>
                        <MenuItem
                          onClick={() => {
                            downloadFile(
                              'api/policies' +
                                fileDownload.href! +
                                getDownloadLinkParams(selectedAgency.encryptedIdentifier, fileDownload.href),
                              `Correspondence_${toFormattedDateAsISO(letter.date)}.pdf`
                            );
                          }}
                        >
                          Download
                        </MenuItem>
                      </ActionsMenu>
                    )}
                  </>
                );
              }}
            </Column>
          </>
        )}
      </PagedView>
    </>
  );
};

export default CorrespondenceSummaries;
